import { isFunction } from 'underscore';
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { withAttentionBeaconPropsInjection } from '@bingads-webui-campaign-react/attention-beacon/src/consumers/with-attention-beacon-props-injection';

const getExtraPropsLocal = ({ needsAttention, props: callTimeProps }) => {
  const { actuationProps: actuationPropsFromCallTimeProps = {} } = callTimeProps;
  return {
    actuationProps: {
      ...actuationPropsFromCallTimeProps,
      needsActivation: actuationPropsFromCallTimeProps.needsActivation || needsAttention,
    },
  };
};

export const withAttentionBeaconActuation = (WrappedComponent) => {
  const WrappedComponentWithActivation = withAttentionBeaconPropsInjection(WrappedComponent);

  const HOC = ({ attentionBeaconProps: attentionBeaconPropsFromProps = {}, ...restProps }) => {
    const {
      getExtraProps: getExtraPropsFromProps,
    } = attentionBeaconPropsFromProps;

    const getExtraProps = useCallback(({ needsAttention, props: callTimeProps }) => {
      const localExtraProps = getExtraPropsLocal({ needsAttention, props: callTimeProps });
      const externalExtraProps = isFunction(getExtraPropsFromProps) ? getExtraPropsFromProps({ needsAttention, props: localExtraProps }) : {};
      // user's extra props can override ours
      return {
        ...localExtraProps,
        ...externalExtraProps,
      };
    }, [getExtraPropsFromProps]);

    const attentionBeaconProps = {
      ...attentionBeaconPropsFromProps,
      getExtraProps,
    };

    return (
      <WrappedComponentWithActivation {...restProps} attentionBeaconProps={attentionBeaconProps} />
    );
  };

  HOC.propTypes = {
    attentionBeaconProps: PropTypes.shape({
      getExtraProps: PropTypes.func,
    }),
  };

  HOC.defaultProps = {
    attentionBeaconProps: {
      getExtraProps: undefined,
    },
  };

  hoistNonReactStatics(HOC, WrappedComponent);
  HOC.displayName = `withAttentionBeaconActuation(${getDisplayName(WrappedComponent)})`;

  return HOC;
};
